import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MentionType } from '../../api/mentions';
import { UserContext } from '../../contexts/user-context';
import { COLORS } from '../../theme/variables';
import { resolveMentionHref } from '../editor/nodes/mention-node';

interface MentionProps {
  mentionType: MentionType;
  id: string;
  displayName: string;
  className?: string;
}

const useStyles = makeStyles({
  mention: {
    userSelect: 'text',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    margin: '0 1px',
    borderRadius: '12px',
    padding: '2px 6px',
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: '16px',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    maxWidth: '100%',
    lineHeight: '1',
  },
  'mention-mentor': {
    backgroundColor: COLORS.COLOR_GREEN_LIGHTENED_45,
    color: COLORS.COLOR_GREEN_BASE,
  },
  'mention-founder': {
    backgroundColor: COLORS.COLOR_BLUE_LIGHTENED_45,
    color: COLORS.COLOR_BLUE_BASE,
  },
  'mention-venture': {
    backgroundColor: COLORS.COLOR_ADDITIONAL_ORANGE_LIGHTENED,
    color: COLORS.COLOR_ADDITIONAL_ORANGE,
  },
  disabled: {
    cursor: 'default',
  },
});

function Mention({ mentionType, id, displayName, className }: MentionProps) {
  const classes = useStyles();
  const { hasAccessToAction } = useContext(UserContext);

  const href = resolveMentionHref(mentionType, id);
  const currentPath = `${window.location.pathname}${window.location.search}`;
  const name = displayName?.replace?.(/_+/g, ' ') || '';

  const hasAccess = useMemo(() => {
    switch (mentionType) {
      case MentionType.MENTOR:
        return hasAccessToAction('mentor.details.view');
      case MentionType.FOUNDER:
        return hasAccessToAction('founder.details.view');
      case MentionType.VENTURE:
        return hasAccessToAction('venture.details.view');
      default:
        return false;
    }
  }, [mentionType, hasAccessToAction]);

  return (
    <Link
      to={{ pathname: href, state: { prevPath: currentPath } }}
      className={cn(
        classes.mention,
        classes[`mention-${mentionType.toLowerCase()}` as keyof typeof classes],
        className,
        !hasAccess && classes.disabled,
      )}
      onClick={(e) => {
        if (!hasAccess) {
          e.preventDefault();
        }
      }}
      data-mention-id={id}
      data-mention-type={mentionType}
      data-prev-path={currentPath}>
      {name}
    </Link>
  );
}

export default Mention;
